import { Suspense, lazy, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Cursor from "@/components/Cursor";
import Preloader from "@/components/Preloader";
import { INTRO_PENDING, markIntroDone } from "@/lib/intro";
import { matchLegacy } from "@/lib/redirects";
import { basePath } from "@/lib/assets";
import SiteMeta from "@/components/SiteMeta";
import Home from "@/pages/Home";

// Home is eager (it is the common entry point); every other route is a separate chunk,
// so a visitor landing on /contact never downloads the case-study or blog code.
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Product = lazy(() => import("@/pages/Product"));
const Products = lazy(() => import("@/pages/Products"));
const SnapxOps = lazy(() => import("@/pages/SnapxOps"));
const Work = lazy(() => import("@/pages/Work"));
const CaseStudy = lazy(() => import("@/pages/CaseStudy"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Legal = lazy(() => import("@/pages/Legal"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const useLenis = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const legacyTarget = matchLegacy(pathname);
    if (legacyTarget) {
      navigate(legacyTarget, { replace: true });
      return;
    }
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigate]);
  return null;
};

/** Holds the dark ground while a route chunk loads, so there is never a white flash. */
/**
 * Shown only on the first visit to a route whose chunk is still downloading. It matches
 * the page background so it reads as "not yet painted" rather than a black flash.
 */
const RouteFallback = () => <div className="min-h-[60vh] bg-ink" aria-hidden="true" />;

const AppShell = () => {
  useLenis();
  const [showIntro, setShowIntro] = useState(INTRO_PENDING);
  const location = useLocation();
  return (
    <div className="cursor-none-desktop">
      <AnimatePresence>
        {showIntro && (
          <Preloader
            onDone={() => {
              markIntroDone();
              setShowIntro(false);
            }}
          />
        )}
      </AnimatePresence>
      <ScrollManager />
      <SiteMeta />
      <Cursor />
      <Navbar />
      {/*
        A single fast fade-in on the incoming page.

        Previously this used AnimatePresence with mode="wait", which held the outgoing
        page for a full exit animation before mounting the next — leaving the screen
        empty in between. Stacked with the curtain overlay it added up to roughly a
        second of black on every menu click. Swapping content immediately and fading it
        in removes the gap entirely.
      */}
      <motion.main
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/eduanant" element={<Product />} />
            <Route path="/products/snapxops" element={<SnapxOps />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:slug" element={<CaseStudy />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.main>
      <Footer />
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
};

function App() {
  // Router base path comes from config (REACT_APP_BASE_PATH), falling back to sniffing
  // the URL so an unconfigured build still works when dropped into a subfolder.
  return (
    // reducedMotion="user": when the device asks for less motion, Framer Motion skips
    // movement (slides, loops, parallax) and keeps only fades. Without it every motion
    // component ignored the setting. The prerenderer relies on this too — it captures
    // with reduced motion on, so each snapshot is the settled layout, identical between
    // builds.
    <MotionConfig reducedMotion="user">
      <div className="App">
        <BrowserRouter basename={basePath()}>
          <AppShell />
        </BrowserRouter>
      </div>
    </MotionConfig>
  );
}

export default App;
